<template lang='pug'>
.stats-by-year(:class="{'dark-mode': isDarkMode}")
  loading(v-if="isLoading")
  .stats(v-else)
    Row
      Column(desktop="6" mobile="6")
        StatBlock(:data="formattedDistance" :label="totalDistanceLabel")
      Column(desktop="6" mobile="6")
        StatBlock(:data="formattedDuration" :label="totalTimeLabel")
      Column(desktop="6" mobile="6")
        StatBlock(:data="formattedCount" :label="workoutsLabel")
      Column(desktop="6" mobile="6")
        StatBlock(:data="formattedCalories" :label="caloriesLabel")
    Row(v-if="device === 'all' && filteredWorkouts.length")
      Column(desktop="12")
        h5.chart-header {{ chartHeader }}
        Field(:label="showEquipmentActivityByLabel")
          RadioButtonGroup(v-model="selectedChart" :options="chartOptions" theme="pill-outline")
        .pie-chart-wrapper(v-if="selectedChart === 'pie'")
          Pie.pie-chart(:data="pieChartData" :options="pieChartOptions")
        .bar-chart-wrapper(v-if="barChartData && selectedChart === 'bar'")
          Bar.bar-chart(:data="barChartData" :options="barChartOptions")
</template>
<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { Bar, Pie } from 'vue-chartjs'
import {
  Chart as ChartJS,
  type ChartData,
  type ChartOptions,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale,
  ArcElement,
} from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import StatBlock from './StatBlock.vue'
import type { TimeRangeFilter, Workout } from './WorkoutTypes'
import { YARDS_PER_METER, formatElapsedTime, mapPieChartData, workoutShare } from './workout-helpers'
import type { WorkoutShare } from './workout-helpers'
import { useRogue } from '@/js/services/Rogue'

ChartJS.register(Tooltip, Legend, BarElement, CategoryScale, LinearScale, ArcElement, ChartDataLabels)

const rogue = useRogue()
const route = useRoute()

const isDarkMode = computed<boolean>(() => route.query.theme === 'dark')

// Translated labels
const totalTimeLabel = computed(() => rogue.__('Total Time'))
const workoutsLabel = computed(() => rogue.__('Workouts'))
const caloriesLabel = computed(() => rogue.__('Calories'))
const showEquipmentActivityByLabel = computed(() => rogue.__('Show Equipment Activity By'))

const props = defineProps<{
  year: number,
  device: string,
  units: 'meters' | 'yards' | 'miles',
  workouts: Workout[],
}>()

const chartOptions = [
  {
    label : 'Percentage',
    value : 'pie',
  },
  {
    label : 'Distance',
    value : 'bar',
  },
]

const filteredWorkouts = ref<Workout[]>([])
const barChartData = ref<ChartData<'bar'> | null>(null)
const pieChartData = ref<ChartData<'pie'> | null>(null)
const isLoading = ref(true)
const selectedChart = ref<'pie' | 'bar'>('pie')

const selectedTimeRange = ref<TimeRangeFilter['value']>('1w')

const formattedUnits = computed<string>(() => props.units.charAt(0).toUpperCase() + props.units.slice(1).toLowerCase())
const chartHeader = computed<string>(() => `${formattedUnits.value} By Type`)

// Each record's share of the chosen machine, or of every cardio machine; never the Air Rhino's
const shares = computed(() => filteredWorkouts.value.map(workout => workoutShare(workout, props.device)))
const sumShares = (read: (share: WorkoutShare) => number): number => shares.value.reduce((sum, share) => sum + read(share), 0)

const totalDistance = computed<number>(() => sumShares(share => share.distance))
const totalDistanceYards = computed<number>(() => totalDistance.value * YARDS_PER_METER)
const formattedDistance = computed<string>(() => {
  if (!totalDistance.value) return '—'

  switch (props.units) {
    case 'yards':
      return Math.round(totalDistanceYards.value).toLocaleString()
    case 'miles':
      return parseFloat((totalDistanceYards.value / 1760).toFixed(2)).toLocaleString()
    case 'meters':
    default:
      return Math.round(totalDistance.value).toLocaleString()
  }
})

const totalDistanceLabel = computed<string>(() => {
  switch (props.units) {
    case 'miles':
      return rogue.__('Total Miles')
    case 'yards':
      return rogue.__('Total Yards')
    case 'meters':
    default:
      return rogue.__('Total Meters')
  }
})

const totalDurationMs = computed<number>(() => sumShares(share => share.durationMs))

const formattedDuration = computed<string>(() => {
  if (!totalDurationMs.value) return '—'

  return formatElapsedTime(totalDurationMs.value)
})

const workoutCount = computed<number>(() => filteredWorkouts.value.length)
const formattedCount = computed<string>(() => workoutCount.value ? workoutCount.value.toLocaleString() : '—')

const totalCalories = computed<number>(() => sumShares(share => share.calories))
const formattedCalories = computed<string>(() => totalCalories.value ? totalCalories.value.toLocaleString() : '—')

const barChartOptions = computed<ChartOptions>(() => {
  const textColor = isDarkMode.value ? '#fff' : '#000'
  const gridColor = isDarkMode.value ? '#444' : '#E9E9E9'

  return {
    indexAxis           : 'y',
    responsive          : true,
    maintainAspectRatio : false,
    scales              : {
      x: {
        stacked : true,
        border  : {
          display: false,
        },
        grid: {
          color : gridColor,
          z     : 1,
        },
        ticks: {
          maxTicksLimit : 5,
          callback      : function (value: string | number) {
            const dist = value as number
            switch (props.units) {
              case 'yards':
                return `${Math.round(dist).toLocaleString()}YDS`
              case 'miles':
                return `${parseFloat(dist.toFixed(2)).toLocaleString()}MI`
              case 'meters':
              default:
                return dist >= 1000
                  ? `${parseFloat((dist / 1000).toFixed(2)).toLocaleString()}K`
                  : `${Math.round(dist).toLocaleString()}M`
            }
          },
          color : textColor,
          font  : {
            family : '"Roboto", Helvetica, Arial, sans-serif',
            size   : 12,
            weight : 'bold',
          },
        },
      },
      y: {
        ticks: {
          color : textColor,
          font  : {
            family : '"Roboto", Helvetica, Arial, sans-serif',
            size   : 12,
            weight : 'bold',
          },
        },
        border: {
          display: false,
        },
        grid: {
          display: false,
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      datalabels: {
        display: false,
      },
    },
  }
})

const pieChartOptions = computed(() => {
  const textColor = isDarkMode.value ? '#fff' : '#000'

  return {
    responsive          : true,
    maintainAspectRatio : false,
    animation           : false,
    layout              : {
      padding: {
        left  : 0,
        right : 0,
      },
    },
    plugins: {
      legend: {
        align    : 'start',
        position : 'right',
        reverse  : true,
        labels   : {
          boxWidth  : 10,
          boxHeight : 10,
          color     : textColor,
          font      : {
            family     : '"Roboto", Helvetica, Arial, sans-serif',
            lineHeight : 1,
            size       : 14,
          },
          padding       : 16,
          pointStyle    : 'circle',
          usePointStyle : true,
        },
      },
      // Configuration for 'chartjs-plugin-datalabels' to display percentages inside the slices
      datalabels: {
        color : '#fff',
        font  : {
          weight : 'normal',
          family : '"Roboto", Helvetica, Arial, sans-serif',
          size   : 16,
        },
        formatter: (value: number, context: any) => {
          const total = context.chart.data.datasets[0].data.reduce((acc: any, val: any) => acc + (val as number), 0)
          const percentage = total > 0 ? (value / total * 100).toFixed(0) : '0'

          return `${percentage}%`
        },
        align  : 'start',
        anchor : 'end',
      } as any,
    },
  }
})

onMounted(() => {
  isLoading.value = true

  // Workout data in props should already be filtered by device type. Just need to filter by year here.
  filteredWorkouts.value = props.workouts
    .filter(workout => {
      if (!workout.data.payload?.createdAt) return false

      const startTime = workout.data.payload.createdAt
      const year = new Date(startTime).getFullYear()

      return year === props.year
    })

  pieChartData.value = mapPieChartData(filteredWorkouts.value, props.units, props.device)
  barChartData.value = {
    labels   : pieChartData.value.labels?.map((l => (l as string).replace('Echo ', ''))),
    datasets : pieChartData.value.datasets.map((ds, index) => ({
      backgroundColor : ds.backgroundColor as string[],
      data            : ds.data,
      label           : pieChartData.value?.labels?.at(index) as string,
    })),
  }

  isLoading.value = false
})
</script>

<style lang="scss" scoped>
.stats-by-year {
  padding: $space-m $space-s;

  .no-workouts {
    align-items: center;
    background-color: $color-gray-x-light;
    border-radius: $space-xxs;
    display: flex;
    flex-direction: column;
    gap: $space-m;
    justify-content: center;
    min-height: 183px;
    width: 100%;
    padding: $space-s;

    @keyframes pulse {
      0%, 100% {
        height: 34px;
      }
      50% {
        height: 15px;
      }
    }

    .bars-wrapper {
      height: 34px;
      display: flex;
      flex-direction: column;
      justify-content: flex-end;

      .bars {
        background: $color-gray-dark;
        width: 10px;
        height: 34px;
        margin: 0 auto;
        position: relative;
        animation: pulse 4s ease-in-out infinite;

        &::before,
        &::after {
          content: "";
          position: absolute;
          width: 10px;
          background: $color-gray-dark;
          bottom: 0;
        }

        &::before {
          height: 24px;
          left: -15px;
          animation: pulse 4s ease-in-out infinite;
          animation-delay: 1s;
        }

        &::after {
          height: 19px;
          right: -15px;
          animation: pulse 4s ease-in-out infinite;
          animation-delay: 2s;
        }
      }
    }

    .message {
      color: $color-gray-dark;
      font-size: $size-xs;
      height: 32px;
      text-align: center;
      max-width: 311px;
    }
  }

  .bar-chart-wrapper {
    background-color: $color-gray-x-light;
    border-radius: $space-xxs;
    height: 225px;
    padding: 0 $space-xs;
  }

  .pie-chart {
    height: 225px;
  }

  .chart-header {
    font-size: $size-s;
  }

  &.dark-mode {
    color: $color-white;

    .bar-chart-wrapper {
      background-color: $color-black-light;
    }

    .chart-header {
      color: $color-white;
    }
  }
}
</style>

<style lang="scss">
/* Increase label size for better readability in year stats */
.stats-by-year .field .label {
  font-size: $size-s !important;
}
</style>
